import { useEffect, useRef } from 'react';
import { api } from '../../services/api';
import { useCustomer } from '../../contexts/CustomerContext';
import { useTenant } from '../../contexts/TenantContext';
import { logger } from '../../utils/logger';

/**
 * Pastille chiffrée sur l'icône de la PWA = notifications non lues.
 *
 * Le chiffre est POSÉ par les service workers quand une notification arrive
 * pendant que l'app est fermée (data.unread_count). Ce composant ferme la
 * boucle : à l'ouverture de l'app (et à chaque retour au premier plan), le
 * client « a vu » ses notifications — il n'existe pas de boîte de réception —
 * donc on les marque lues côté serveur et on efface le badge. Le chiffre ne
 * représente ainsi que ce qui est arrivé DEPUIS la dernière ouverture, et il
 * reste juste même si une notification a été reçue téléphone éteint ou lue sur
 * un autre appareil (ajout du 31/08/2026).
 *
 * Rendu au-dessus des routes ; ne fait rien si aucun client n'est connecté.
 */
export default function NotificationBadgeSync() {
  const { customer } = useCustomer();
  const { tenant } = useTenant();
  const customerId = customer?.id;
  const tenantId = tenant?.id;
  const syncing = useRef(false);

  useEffect(() => {
    const clear = () => {
      if ('clearAppBadge' in navigator) (navigator as any).clearAppBadge().catch(() => {});
    };
    if (!customerId || !tenantId) {
      clear(); // déconnexion : pas de badge fantôme
      return;
    }
    const sync = async () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (syncing.current) return;
      syncing.current = true;
      try {
        await api.patch('/notifications/read-all', {}, { headers: { 'X-Tenant-ID': tenantId } });
        clear();
      } catch (err) {
        logger.warn('[badge] synchro non aboutie', err);
      } finally {
        syncing.current = false;
      }
    };
    sync(); // à l'ouverture
    const onVisible = () => { if (document.visibilityState === 'visible') sync(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [customerId, tenantId]);

  return null;
}
